// =============================================================================
// SystemSettingsAdmin — the "Monitoring" tab on System Content
// =============================================================================
// Holds the monitored-service setting:
//   • Monitored DFV Service Alert Message — the wording a responder sees when a
//     service is flagged "refer to management" and they try to Approve/Decline
//     (they can then only select "Needs Info"). Stored in `portal_config` under
//     `monitored_shelter_alert` as a plain string; the request approval flow
//     reads the same key.
//
// The System Alert email address + the Feedback notification/review settings that
// used to live here moved to Manage → System Setup (Alerts + Feedback Module
// tabs) on 23 Jul 2026, and the read-only failed-notifications list moved to
// System Setup → Alerts on 26 Jul 2026. Their config keys / value shapes / audit
// actions are unchanged — only the editing/viewing location moved.
// =============================================================================
import React from "react";
import { getConfigValue, upsertConfig } from "../../data/portalConfig";
import {
  DEFAULT_MONITORED_SHELTER_ALERT,
  MONITORED_SHELTER_ALERT_KEY,
} from "../../services/approvalHold";
import { logAudit } from "../../utils/auditLog";
import { Btn } from "../../utils/ui.jsx";

export function SystemSettingsAdmin({ user }) {
  const [loading, setLoading] = React.useState(true);
  const [monitoredAlert, setMonitoredAlert] = React.useState("");
  // The last-saved value — the baseline to revert to on Cancel.
  const [savedValue, setSavedValue] = React.useState("");
  // Read-only until the user clicks Edit (so a change here is deliberate).
  const [editing, setEditing] = React.useState(false);
  const [savingMon, setSavingMon] = React.useState(false);
  const [savedMonMsg, setSavedMonMsg] = React.useState("");

  React.useEffect(function () {
    getConfigValue(MONITORED_SHELTER_ALERT_KEY).then(function (val) {
      const text = typeof val === "string" && val ? val : DEFAULT_MONITORED_SHELTER_ALERT;
      setMonitoredAlert(text);
      setSavedValue(text);
      setLoading(false);
    });
  }, []);

  function startEdit() {
    setSavedMonMsg("");
    setEditing(true);
  }

  function cancelEdit() {
    setMonitoredAlert(savedValue);
    setSavedMonMsg("");
    setEditing(false);
  }

  function saveMonitored() {
    const text = (monitoredAlert || "").trim();
    if (!text) {
      setSavedMonMsg("The message can't be empty.");
      setTimeout(function () {
        setSavedMonMsg("");
      }, 4000);
      return;
    }
    setSavingMon(true);
    // Stored as a plain string (the request approval flow reads it as one).
    upsertConfig(MONITORED_SHELTER_ALERT_KEY, text).then(function (r) {
      setSavingMon(false);
      if (r.error) {
        setSavedMonMsg(`Save failed: ${r.error.message}`);
      } else {
        setSavedValue(text);
        setMonitoredAlert(text);
        setEditing(false);
        setSavedMonMsg("Saved ✓");
        logAudit("monitored_shelter_alert_updated", { by: user?.shelterName });
      }
      setTimeout(function () {
        setSavedMonMsg("");
      }, 3000);
    });
  }

  if (loading) return <div className="text-zephyr-muted p-5">Loading...</div>;

  return (
    <div className="max-w-[900px]">
      <p className="m-0 mb-4 text-[13px] text-zephyr-muted">
        When a shelter is flagged "No requests for this shelter to be approved — refer to
        management", a responder who tries to Approve or Decline an item sees this message and can
        only select "Needs Info". Edit the wording shown here.
      </p>
      <label
        htmlFor="monitored-shelter-alert"
        className="text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1"
      >
        Monitored DFV Service Alert Message
      </label>
      <textarea
        id="monitored-shelter-alert"
        value={monitoredAlert}
        disabled={!editing}
        onChange={function (e) {
          setMonitoredAlert(e.target.value);
        }}
        rows={4}
        style={{ fontFamily: "inherit" }}
        className="w-full max-w-[600px] py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
      />
      <div className="flex gap-[12px] mt-[14px] items-center">
        {editing ? (
          <>
            <Btn onClick={saveMonitored} disabled={savingMon}>
              {savingMon ? "Saving..." : "Save"}
            </Btn>
            <button
              type="button"
              onClick={cancelEdit}
              className="bg-white text-zephyr-text rounded-[8px] px-4 py-[7px] text-[13px] font-semibold cursor-pointer"
              style={{ border: "1px solid #CFE0DE" }}
            >
              Cancel
            </button>
          </>
        ) : (
          <Btn onClick={startEdit}>Edit</Btn>
        )}
        {savedMonMsg && (
          <span className="text-[#2E7D32] font-semibold text-[13px]">{savedMonMsg}</span>
        )}
      </div>
    </div>
  );
}
